import { Icon, Toggle } from '@advanza/ui'
import clsx from 'clsx'
import Restricted from 'components/Restricted'
import CountryToggle from 'components/ui/CountryToggle'
import OfficeLogo from 'components/ui/OfficeLogo'
import { hasAccess, isProfileBuilder, isSuperUser } from 'misc/user'
import { Link } from 'react-router-dom'
import style from './navigation.module.css'

const Navigation = ({ current, isCollapsed, children }) => {
    const items = [
        {
            title: 'Dashboard',
            url: '/',
        },
        {
            title: 'Statistics',
            url: '/statistics',
            icon: 'assessment',
            sub: [
                {
                    title: 'Closed loop - Emails',
                    url: '/statistics/closed-loop-emails',
                },
                {
                    title: 'Closed loop - Requests',
                    url: '/statistics/closed-loop-requests',
                },
            ],
        },
        {
            title: 'Sales',
            url: '/sales',
            icon: 'how_to_reg',
            sub: [
                { title: 'Leads', url: '/sales/leads' },
                { title: 'Site info', url: '/sales/site-info' },
                { title: 'Opportunities', url: '/sales/opportunities' },
                { title: 'Lead Estimator', url: '/sales/lead-estimator' },
            ],
        },
        {
            title: 'Customer service',
            url: '/service',
            icon: 'domain',
            profileBuilder: true,
            sub: [
                { title: 'Requests', url: '/service/requests' },
                { title: 'Providers', url: '/service/providers', profileBuilder: true },
                {
                    title: 'Demand users',
                    url: '/service/demand-users',
                    restrictedLocation: 'demand_users',
                },
                { title: 'Events/emails', url: '/service/events' },
                { title: 'Matches', url: '/service/matches' },
                { title: 'Cancellations', url: '/service/cancellations' },
                { title: 'Limit reached', url: '/service/limit-reached' },
                { title: 'Active pauses', url: '/service/active-pauses' },
                {
                    title: 'Profile builder wizard',
                    url: '/service/profile-builder-wizard',
                    profileBuilder: true,
                },
            ].concat(
                hasAccess('profile_builder_wizard_config')
                    ? {
                          title: 'Profile builder wizard config',
                          url: '/service/providers-builders',
                          profileBuilder: true,
                      }
                    : [],
            ),
        },
        {
            title: 'Billing',
            url: '/billing',
            icon: 'payment',
            sub: [
                { title: 'Generate invoices', url: '/billing/open' },
                { title: 'Billing run', url: '/billing/run' },
                { title: 'Credit note SEPAs', url: '/billing/sepa' },
                { title: 'Invoices', url: '/billing/invoices' },
                {
                    title: 'Lost Invoices',
                    url: '/billing/lost-invoices',
                    restrictedLocation: 'admin',
                },
                {
                    title: 'Exact export',
                    url: '/billing/exact-export',
                    restrictedLocation: 'export',
                },
                { title: 'Reclaims', url: '/billing/reclaims' },
                { title: 'Debtors', url: '/billing/debtor-new' },
                {
                    title: 'Quarantined',
                    url: '/billing/quarantined',
                    restrictedLocation: 'providers',
                },
            ],
        },
        {
            title: 'Settings',
            url: '/settings',
            icon: 'settings',
            sub: [
                { title: 'Services', url: '/settings/services' },
                { title: 'Country', url: '/settings/country' },
                { title: 'Regions', url: '/settings/regions' },
                { title: 'Licences', url: '/settings/licences' },
                { title: 'Licences Updaters', url: '/settings/licences-updaters' },
                { title: 'Labels', url: '/settings/labels' },
                { title: 'Statuses', url: '/settings/statuses' },
                { title: 'Marketing Promos', url: '/settings/marketing-promos' },
                { title: 'Admin Users', url: '/settings/admin-users' },
                { title: 'Pro Dashboard', url: '/settings/pro-dashboard' },
                { title: 'Reviews', url: '/settings/reviews' },
                { title: 'Review sources', url: '/settings/review-sources' },
                { title: 'SEO', url: '/settings/seo' },
                { title: 'Expertise keywords', url: '/settings/expertise-keywords' },
                {
                    title: 'Ranking checker',
                    url: '/settings/matching-debugger',
                    restrictedLocation: 'providers',
                },
                {
                    title: 'Apis',
                    url: '/settings/apis',
                    restrictedLocation: 'admin',
                },
                {
                    title: 'Partner request push',
                    url: '/settings/partner-request-push',
                    restrictedLocation: 'admin',
                },
                { title: 'Provider Notifications', url: '/settings/provider-notifications' },
                { title: 'Winback Offers', url: '/settings/winback-offers' },
                { title: 'Churn Reason Themes', url: '/settings/churn-reason-themes' },
                {
                    title: 'Free Lead Types',
                    url: '/settings/free-lead-types',
                    restrictedLocation: 'settings',
                },
            ],
        },
        {
            title: 'Ads',
            url: '/ads',
            icon: 'build',
            sub: [
                { title: 'Channable feed', url: '/ads/channable-feed' },
                { title: 'Expectation settings', url: '/ads/expectation-settings' },
                {
                    title: 'Keyword mapping',
                    url: '/ads/keyword-mappings',
                    restrictedLocation: 'keyword_mappings',
                },
                { title: 'Ad sheets', url: '/ads/adsheets', restrictedLocation: 'export' },
                { title: 'Geo targeting', url: '/ads/geo-targeting' },
            ],
        },
        {
            title: 'Marketing',
            url: '/marketing',
            icon: 'campaign',
            sub: [
                { title: 'Email builder', url: '/marketing/email-builder' },
                { title: 'Anonymized requests', url: '/marketing/anonymized-requests' },
            ],
        },
        {
            title: 'Dev',
            url: '/dev',
            icon: 'hot_tub',
            isHidden: () => !hasAccess('dev'),
            sub: [{ title: 'Queue', url: '/dev/queue' }],
        },
        {
            title: 'Reports',
            url: '/reports',
            icon: 'assessment',
            isHidden: () => !hasAccess('export', 'r'),
            sub: [
                { title: 'Presence Subscription Analysis', url: '/reports/subscription-churn' },
                { title: 'Win-Back Offers Report', url: '/reports/win-back-offers' },
                { title: 'Fingerprint sign-up report', url: '/reports/fingerprint-signup' },
                { title: 'Chatbot Analytics', url: '/reports/chatbot-analytics' },
                { title: 'AI Auto Reply Analytics', url: '/reports/ai-auto-reply-analytics' },
                { title: 'Trust check dry run', url: '/reports/trust-check' },
            ],
        },
        {
            title: 'App Analytics',
            url: '/app-analytics',
            icon: 'phone_iphone',
            isHidden: () => !hasAccess('admin', 'r'),
            sub: [
                { title: 'Supply', url: '/app-analytics/supply', restrictedLocation: 'admin' },
                { title: 'Demand', url: '/app-analytics/demand', restrictedLocation: 'admin' },
            ],
        },
        {
            title: 'Design System',
            url: '/design-system',
            icon: 'palette',
            isHidden: () => !hasAccess('design_system'),
            sub: [
                { title: 'Foundations', url: '/design-system/foundations' },
                { title: 'Components', url: '/design-system/components' },
            ],
        },
        {
            title: 'Misc',
            url: '/misc',
            icon: 'set_meal',
            isHidden: () => !hasAccess('export', 'r'),
            sub: [{ title: 'stuff', url: '/misc' }],
        },
    ]
    const isTest = !window.location.hostname.startsWith('office')
    const isAlt = localStorage.getItem('alt_sidebar') === 'true'
    return (
        <div
            className={clsx(
                style.root,
                isCollapsed && style.collapsed,
                isTest && style.isTest,
                isAlt && style.alt,
            )}>
            <div className={style.logo}>
                <OfficeLogo />
                {children}
            </div>
            <div className={style.countryToggle}>
                <CountryToggle inline />
            </div>
            <div className={style.toggles}>
                {items.map(({ title, url, icon, profileBuilder, sub }) => {
                    if (sub && (!isProfileBuilder() || profileBuilder)) {
                        const isCurrent =
                            !isCollapsed && (current || window.location.pathname).startsWith(url)
                        const content = (
                            <div className={clsx(style.content, isCurrent && style.open)}>
                                <Icon fontSize={18} name={icon} />
                                <h5>{title}</h5>
                            </div>
                        )
                        const subs = sub.map(
                            ({ title, url, superUserOnly, restrictedLocation, profileBuilder }) => {
                                const isCurrentSub = (
                                    current || window.location.pathname
                                ).startsWith(url)
                                const subLink = (
                                    <Link
                                        className={isCurrentSub ? style.active : ''}
                                        key={url}
                                        to={url}>
                                        {title}
                                    </Link>
                                )

                                if (superUserOnly && !isSuperUser()) {
                                    return null
                                }
                                if (restrictedLocation) {
                                    return (
                                        <Restricted
                                            key={url}
                                            location={restrictedLocation}
                                            checkWrite={false}>
                                            {subLink}
                                        </Restricted>
                                    )
                                }
                                if (!isProfileBuilder() || profileBuilder) {
                                    return subLink
                                }
                                return null
                            },
                        )
                        return (
                            <Toggle key={url} openNow={isCurrent} title={content}>
                                <div className={style.subs}>{subs}</div>
                            </Toggle>
                        )
                    }
                })}
            </div>
        </div>
    )
}

export default Navigation
